import { animated, useTransition } from "@react-spring/web";
import { ArrowLeft } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { Outlet, useLocation } from "react-router";
import type { MeUserDataType } from "src/app";
import { EvaluationBanner } from "src/components/EvaluationBanner";
import { Footer } from "src/components/footer/index";
import { Header } from "src/components/header";
import { LeftNavigation } from "src/components/left-navigation";
import { PageSuspense } from "src/components/ui/page-suspense";
import { Toaster } from "src/components/ui/toaster";
import { NavigationProvider, useNavigation } from "src/context/NavigationContext";

import { SettingsPageQuery } from "./__generated__/SettingsPageQuery.graphql";
import SettingsSidebar from "./components/SettingsSidebar";
import { SettingsProvider } from "./context/SettingsContext";

// Simplified query that only fetches what's needed for the sidebar
const settingsPageQuery = graphql`
  query SettingsPageQuery {
    me {
      ...appMe
      isSuperAdmin
      organization {
        id
      }
    }
  }
`;

// Content component that renders the Outlet
function SettingsPageContent() {
  return (
    <div className="w-full overflow-auto custom-scrollbar">
      <div className="mx-auto w-full">
        <Outlet />
      </div>
    </div>
  );
}

// Create the settings sidebar
function SettingsSidebarWrapper() {
  const data = useLazyLoadQuery<SettingsPageQuery>(
    settingsPageQuery,
    {}
  );

  const isSuperAdmin = data.me?.isSuperAdmin || false;

  return <SettingsSidebar showAdminSettings={isSuperAdmin} isSuperAdmin={isSuperAdmin} />;
}

// Wrapper component that combines sidebar and content with shared context
function SettingsPageWithSharedContext() {
  const { isMobile } = useNavigation();
  const location = useLocation();

  // On mobile, show menu when on /settings index, show content when on /settings/*
  const isSettingsIndex = location.pathname === "/settings" || location.pathname === "/settings/";
  const showMobileMenu = isMobile && isSettingsIndex;

  // Track previous state for animation direction and initial mount
  const prevShowMenuRef = useRef(showMobileMenu);
  const isInitialMount = useRef(true);

  // Calculate slide direction synchronously (not via state) so it's correct on first render
  // Forward = going from menu to content (prevShowMenu was true, now false)
  // Back = going from content to menu (prevShowMenu was false, now true)
  const slideDirection = prevShowMenuRef.current && !showMobileMenu ? "forward" : "back";

  // After mount, allow animations for subsequent navigations
  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  // Update ref after render so next navigation knows the previous state
  useEffect(() => {
    prevShowMenuRef.current = showMobileMenu;
  }, [showMobileMenu]);

  // React Spring transition for mobile views
  const mobileTransitions = useTransition(showMobileMenu, {
    from: {
      transform: slideDirection === "forward" ? "translateX(-100%)" : "translateX(100%)",
      opacity: 0,
    },
    enter: {
      transform: "translateX(0%)",
      opacity: 1,
    },
    leave: {
      transform: slideDirection === "forward" ? "translateX(100%)" : "translateX(-100%)",
      opacity: 0,
    },
    config: { tension: 300, friction: 30 },
    immediate: isInitialMount.current,
  });

  const sidebarContent = (
    <PageSuspense>
      <SettingsSidebarWrapper />
    </PageSuspense>
  );

  const mainContent = (
    <PageSuspense>
      <SettingsPageContent />
    </PageSuspense>
  );

  return (
    <SettingsProvider>
      <div className="flex h-full overflow-hidden w-full">
        {/* Desktop: show sidebar and content side by side */}
        {!isMobile && (
          <>
            <div className="px-4 pt-2 bg-background border-r border-border w-[280px] min-w-[280px] h-full">
              {sidebarContent}
            </div>
            {mainContent}
          </>
        )}

        {/* Mobile: animated sliding views */}
        {isMobile && (
          <div className="relative flex-1 overflow-hidden">
            {mobileTransitions((style, showMenu) =>
              showMenu ? (
                <animated.div
                  style={style}
                  className="absolute inset-0 px-4 pt-2 overflow-y-auto bg-background"
                >
                  {sidebarContent}
                </animated.div>
              ) : (
                <animated.div
                  style={style}
                  className="absolute inset-0 flex flex-col bg-background overflow-y-auto"
                >
                  {mainContent}
                </animated.div>
              )
            )}
          </div>
        )}
      </div>
    </SettingsProvider>
  );
}

function MobileSettingsHeaderBar() {
  const { mobileConversationHeader } = useNavigation();

  if (!mobileConversationHeader) return null;

  return (
    <nav className="h-11 bg-background shadow-lg relative">
      <div className="absolute left-0 right-0 bottom-0 h-[1px] bg-border shadow-[0_0_5px_rgba(0,0,0,0.1)]"></div>
      <div className="h-full mx-auto flex w-full items-center px-2">
        <button
          onClick={mobileConversationHeader.onBack}
          className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent focus:outline-none transition-colors ml-1.5 mr-3"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <span className="text-base font-medium text-foreground truncate">
          {mobileConversationHeader.title}
        </span>
      </div>
    </nav>
  );
}

function SettingsPageLayoutContent({ page, me }: { page: React.ReactElement, me: MeUserDataType }) {
  const { isLeftNavCollapsed, isMobile, mobileConversationHeader } = useNavigation();

  const showMobileHeader = isMobile && mobileConversationHeader;

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      <LeftNavigation isCollapsed={isLeftNavCollapsed} />
      <div className="flex flex-col flex-1 h-screen">
        {!showMobileHeader && <Header me={me} />}
        {showMobileHeader && <MobileSettingsHeaderBar />}
        <EvaluationBanner suspendedAt={me.organization?.suspendedAt ?? null} />
        <main className="flex-1 flex flex-col h-full relative max-h-full overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br dark:from-slate-900/5 dark:to-slate-950/10 pointer-events-none"></div>
          {page}
        </main>
        {!showMobileHeader && <Footer />}
      </div>
      <Toaster />
    </div>
  );
}

// The layout wrapper for the entire settings page
export const getSettingsPageLayout = (page: React.ReactElement, me: MeUserDataType) => {
  return (
    <NavigationProvider>
      <SettingsPageLayoutContent page={page} me={me} />
    </NavigationProvider>
  );
};

// Create a wrapper component that preserves the getLayout property
const SettingsPage: React.FC & { getLayout: typeof getSettingsPageLayout } = () => {
  return <SettingsPageWithSharedContext />;
};

SettingsPage.getLayout = getSettingsPageLayout;

export default SettingsPage;